












































































































import TranscriptPart from './TranscriptPart.vue'
import Popup from '../popup/Popup.vue'
import TagPopup from '../popup/TagPopup.vue'
import AudioPlayer from '../AudioPlayer.vue'
import TextPopup from '../popup/TextPopup.vue'
import {
  computed,
  ComputedRef,
  defineComponent,
  onMounted,
  ref,
  toRefs,
  watch
} from '@vue/composition-api'
import {
  processTextSelection,
  TextSelection
} from '../../composables/processTextSelection'
import { clearTextSelection } from '../../composables/clearTextSelection'
import { useActions } from '../../store/useActions'
import { useGetters } from '../../store/useGetters'
import { Tag, UITag } from '../../types/tag'
import { getTextSelectionRange } from '../../composables/getTextSelectionRange'
import { isSelectionEmpty } from '../../utils/selection'
import { getWordIndex } from '../../utils/tags'
import useTextEditor from '../../composables/useTextEditor'
import { UITranscript } from '../../types/transcript'
import { useIndexedDb } from '../../composables/useIndexedDb'
import { UISpeaker, Word } from '../../models/word'
import KaiEditor from '../../components/editor/KaiEditor.vue'
import DownvotePopUp from '../popup/DownvotePopUp.vue'
import {
  NONE_SUB_CATEGORY,
  PARTIAL_SUB_CATEGORY
} from '../../store/modules/transcriptTags'

export default defineComponent({
  name: 'KaiConversation',
  props: {
    transcript: {
      required: true,
      type: Object as () => UITranscript,
      default: null
    },
    audioUrl: {
      required: true,
      type: String
    }
  },
  components: {
    KaiEditor,
    TranscriptPart,
    AudioPlayer,
    TagPopup,
    Popup,
    TextPopup,
    DownvotePopUp
  },
  setup(props, { root, emit, ...other }) {
    const { transcript } = toRefs(props)
    const conversationBg = ref<HTMLElement | null>()
    const audioTime = ref<number>()
    const player = ref<any | null>(null)
    const isPlaying = ref<boolean>(false)
    const isTagPopupShown = ref<boolean>(false)
    const activeSpeakerBtn = ref<HTMLElement | null>(null)
    const selectedTag = ref<UITag | null>(null)
    const selectedTagIndex = ref<number | null>(null)
    const tagPopupTarget = ref<DOMRect | null>(null)
    const speakerPopupTarget = ref<DOMRect | null>(null)
    const selectedSpeakerTimecode = ref<number | null>(null)
    const wordClicksCount = ref<number>(0)
    const timer = ref()
    const version = ref(0)
    const partsWords = ref<Word[][]>([])
    const isTimecodePopupShown = ref<boolean>(false)
    const shownWord = ref<Word | null>(null)
    const wordPopupTarget = ref<DOMRect | null>(null)
    const autosaveInterval = ref<number | undefined>()
    const isDownvotePopupVisible = ref<boolean>(false)
    const downvotePopupTarget = ref(null)

    const actions = useActions({
      removeTag: 'transcriptTags/removeTag',
      cancelSelection: 'conversation/cancelSelection',
      setSelectionBoundaries: 'conversation/setSelectionBoundaries',
      toggleSpeakersList: 'conversation/toggleSpeakersList',
      setCurrentSelection: 'conversation/setCurrentSelection',
      setSelection: 'selection/setSelection',
      setPart: 'selection/setPart',
      clearSelection: 'selection/clearSelection',
      toggleSelectionPopup: 'selection/togglePopup',
      toggleTagsPicker: 'tagsPicker/toggle',
      initTagEdit: 'tagsPicker/initTagEdit',
      finishTagEdit: 'tagsPicker/finishTagEdit',
      updateTranscript: 'conversation/updateTranscript',
      reinitializeTags: 'transcriptTags/reinitializeTags',
      closeTagEdit: 'tagsPicker/closeTagEdit',
      undo: 'conversation/undo',
      removeLastHistoryItem: 'conversation/removeLastHistoryItem',
      updateTranscriptHistory: 'conversation/updateTranscriptHistory',
      registerAudioRewindCallback: 'editor/registerAudioRewindCallback',
      updateTags: 'transcriptTags/updateTags'
    })

    const getters = useGetters({
      isSpeakersListVisible: 'conversation/isSpeakersListVisible',
      isEditable: 'conversation/isEditable',
      selectionBoundaries: 'selection/boundaries',
      selectionPopupShown: 'selection/popupShown',
      tagsPickerActive: 'tagsPicker/isActive',
      analysisActive: 'tagsPicker/analysisActive',
      shouldSave: 'conversation/shouldSave',
      shouldUndo: 'conversation/shouldUndo',
      spelledWords: 'conversation/spelledWords',
      appliedTags: 'transcriptTags/appliedTags',
      isSafari: 'conversation/isSafari',
      transcriptHistory: 'conversation/transcriptHistory',
      transcriptReady: 'conversation/transcriptReady',
      transcript: 'conversation/transcript',
      range: 'selection/range',
      topics: 'tagsPicker/topicsCategories'
    })

    const subCategoryOptions = computed(() => {
      if (!selectedTag.value) {
        return []
      }

      return getters.topics.value.flatMap(t =>
        t.topics
          .filter(
            a => a.category === selectedTag.value?.category && !!a.sub_category
          )
          .map(a => a.sub_category)
      )
    })

    const showDownvotePopup = () => {
      isDownvotePopupVisible.value = true
      activateTagsPicker()
    }

    interface UpdateNoteTagInterface {
      selectedOption: string
    }

    const hideDownvotePopup = (data?: UpdateNoteTagInterface) => {
      isDownvotePopupVisible.value = false

      if (data && data.selectedOption !== undefined) {
        if (selectedTag?.value?.subCategory !== data.selectedOption) {
          // data prep before update
          if (data.selectedOption === PARTIAL_SUB_CATEGORY)
            selectedTag.value.note = PARTIAL_SUB_CATEGORY
          else {
            selectedTag.value.note = !selectedTag.value.note
              ? selectedTag.value.subCategory
              : selectedTag.value.note

            selectedTag.value.subCategory = data.selectedOption
            selectedTag.value.title = data.selectedOption
          }

          // database update
          emit(
            'tag-note',
            selectedTag.value,
            selectedTag.value.note,
            data.selectedOption === PARTIAL_SUB_CATEGORY
              ? selectedTag.value.subCategory
              : data.selectedOption,
            data.selectedOption === NONE_SUB_CATEGORY ||
              data.selectedOption === PARTIAL_SUB_CATEGORY
          )

          // UI updates
          actions.updateTags({
            prevTags: [selectedTag.value],
            newTags: [selectedTag.value]
          })

          if (
            data.selectedOption === NONE_SUB_CATEGORY ||
            data.selectedOption === PARTIAL_SUB_CATEGORY
          ) {
            emit('delete-tag', selectedTag.value)
            actions.removeTag({
              tag: selectedTag.value,
              index: selectedTagIndex.value
            })
          }
        }
      }

      closeTagPopup()
    }

    const {
      handleEditorKeydown,
      handleEditorInput,
      conversationRef,
      setMouseCursor,
      formatAll,
      transcript: editorTranscript,
      updTranscript,
      pasteFromClipboard,
      ...textEditor
    } = useTextEditor(getters.transcript.value, getters.isSafari.value)

    const { initDB, putTranscript } = useIndexedDb()

    const closeTagPopup = () => {
      if (!isTagPopupShown.value) {
        return
      }

      isTagPopupShown.value = false
      actions.cancelSelection()
    }

    function hideWordPopup() {
      isTimecodePopupShown.value = false
      shownWord.value = null
      wordPopupTarget.value = null
    }

    const displayedTranscript: ComputedRef<UITranscript | null> = computed(
      () => {
        return getters.isEditable.value
          ? editorTranscript.value
          : transcript.value
      }
    )

    // Get words of the part
    function getWords(part: Word): Word[] {
      if (!displayedTranscript.value) {
        return []
      }

      if (
        part.startWordIndex === undefined ||
        part.endWordIndex === undefined
      ) {
        return []
      }

      return displayedTranscript.value.words.slice(
        part.startWordIndex,
        part.endWordIndex + 1
      )
    }

    function clearSelection() {
      actions.clearSelection(true)
      clearTextSelection()
    }

    function activateTagsPicker() {
      actions.toggleTagsPicker(true)
    }

    function hideSelectionPopup(e: MouseEvent) {
      const tagPickerClicked = !!e.composedPath().find((p: EventTarget) => {
        const el = p as HTMLElement
        return el.id === 'all-tags-section'
      })

      if (tagPickerClicked) {
        return
      }

      actions.toggleTagsPicker(false)
      actions.closeTagEdit()
      clearSelection()
    }

    function showSelectionPopup(selection: TextSelection) {
      closeTagPopup()
      actions.setSelection(selection)
      actions.toggleSelectionPopup(true)
    }

    function handleTextSelection(e: Event) {
      hideWordPopup()
      const selection: TextSelection | null = processTextSelection()
      if (!selection || isSelectionEmpty(selection)) {
        clearSelection()
        actions.toggleTagsPicker(false)
        actions.closeTagEdit()
        return
      }

      if (getters.isEditable.value) {
        return
      }

      showSelectionPopup(selection)
    }

    function navigateToWord(word: Word) {
      if (player.value) {
        player.value.setPlaybackTime(
          word.start / 1000,
          !getters.isEditable.value
        )
      }
    }

    function handleWordClick(word: Word) {
      if (getters.isEditable.value) {
        navigateToWord(word)
        return
      }

      wordClicksCount.value += 1
      if (wordClicksCount.value === 1) {
        timer.value = setTimeout(() => {
          navigateToWord(word)
          wordClicksCount.value = 0
        }, 200)
      } else {
        clearTimeout(timer.value)
        wordClicksCount.value = 0
        return
      }
    }

    function setPlayerStatus(newStatus: boolean) {
      if (isPlaying.value !== newStatus) {
        isPlaying.value = newStatus
      }
    }

    function showTagPopup(tag: UITag, index: number, target: HTMLElement) {
      if (!tag) {
        return
      }
      isTagPopupShown.value = true
      selectedTag.value = tag
      selectedTagIndex.value = index
      tagPopupTarget.value = target.getBoundingClientRect()
    }

    function showTimecodesPopup(word: Word, target: HTMLElement) {
      isTimecodePopupShown.value = true
      shownWord.value = word
      wordPopupTarget.value = target.getBoundingClientRect()
    }

    function handleTagHover({
      e,
      tags,
      word,
      speakerId,
      speakerName
    }: {
      e: Event
      tags: UITag[]
      word: Word
      speakerId: number
      speakerName: string
    }) {
      if (getters.isEditable.value) {
        return
      }
      // prevent from showing when text is selected or word has less or more than 1 tag
      const range = getTextSelectionRange()

      if (!tags || !tags.length || range || getters.tagsPickerActive.value) {
        if (!getters.selectionPopupShown.value) {
          const targetWord: HTMLElement = e.target as HTMLElement
          showTimecodesPopup(word, targetWord)
        }
        return
      }

      if (tags.length !== 1) {
        closeTagPopup()
        return
      }

      const targetWord: HTMLElement = e.target as HTMLElement
      const wordIndex = getWordIndex(
        targetWord,
        displayedTranscript.value?.words || []
      )

      if (wordIndex === -1) {
        return
      }
      tags[0].speaker = speakerName
      showTagPopup(tags[0], wordIndex, targetWord)
    }

    function handleTagClick({
      event,
      tag,
      word,
      speakerId,
      speakerName
    }: {
      event: Event
      tag: any
      word: Word
      speakerId: number
      speakerName: string
    }) {
      if (getters.isEditable.value) {
        return
      }

      // Hide the downvote popup if it's visible
      hideDownvotePopup()

      const target: HTMLElement = event.target as HTMLElement
      const wordIndex = getWordIndex(
        word,
        displayedTranscript.value?.words || []
      )
      if (wordIndex === -1) {
        return
      }
      tag.items[0].speaker = speakerName
      showTagPopup(tag.items[0], wordIndex, target)
    }

    function handleTagLeave({
      e,
      tags
    }: {
      e: MouseEvent
      tags: UITag[]
      word: Word
    }) {
      if (!displayedTranscript.value) {
        return
      }
      if (isTimecodePopupShown.value) {
        hideWordPopup()
        return
      }

      const targetTag: Tag | null = tags && tags.length ? tags[0] : null
      const leaveTo: HTMLElement | null = e.relatedTarget
        ? (e.relatedTarget as HTMLElement)
        : null

      if (!leaveTo || !targetTag) {
        return
      }

      const targetClasses = Array.from(leaveTo.classList)

      if (
        targetClasses.includes('text-item') &&
        leaveTo.dataset.timecodeStart
      ) {
        if (targetClasses.includes('space')) {
          const targetWordIndex = displayedTranscript.value.words.findIndex(
            (w: Word) =>
              w.end === parseInt(leaveTo.dataset.timecodeStart as string)
          )

          if (
            targetWordIndex >= targetTag.startWordIndex &&
            targetWordIndex < targetTag.endWordIndex
          ) {
            return
          }
        }

        if (targetClasses.includes('word')) {
          const targetWordIndex = displayedTranscript.value.words.findIndex(
            (w: Word) =>
              w.start === parseInt(leaveTo.dataset.timecodeStart as string)
          )
          if (
            targetWordIndex >= targetTag.startWordIndex &&
            targetWordIndex <= targetTag.endWordIndex
          ) {
            return
          }
        }
      }

      if (
        targetClasses.includes('popup') ||
        targetClasses.includes('tags-popup')
      ) {
        return
      }

      if (!getters.tagsPickerActive.value) {
        closeTagPopup()
        selectedTag.value = null
        selectedTagIndex.value = null
      }
    }

    function handleTagPopupLeave() {
      if (getters.tagsPickerActive.value) {
        return
      } else {
        closeTagPopup()
      }
    }

    function handleTagDelete() {
      if (!selectedTag.value) {
        return
      }
      emit('delete-tag', selectedTag.value)
      actions.removeTag({
        tag: selectedTag.value,
        index: selectedTagIndex.value
      })
      closeTagPopup()
    }

    function upvote() {
      if (!selectedTag.value) {
        return
      }
      selectedTag.value.flagged = true
      emit('upvote-tag', selectedTag.value)
    }

    function downvote() {
      isTagPopupShown.value = false
      showDownvotePopup()
      downvotePopupTarget.value = tagPopupTarget.value
    }

    function handleTopicEdit() {
      actions.toggleTagsPicker(true)
      actions.initTagEdit({
        tag: selectedTag.value,
        index: selectedTagIndex.value
      })
    }

    function showSpeakers(timecode: number, event: Event) {
      activeSpeakerBtn.value = event.target as HTMLElement
      speakerPopupTarget.value = (event.target as HTMLElement).getBoundingClientRect()
      selectedSpeakerTimecode.value = timecode
      actions.toggleSpeakersList(timecode)
    }

    async function setSpeaker(speaker: UISpeaker) {
      if (!getters.transcript) {
        return
      }
      const partToEditInTranscript = getters.transcript.value.parts.find(
        (p: Word) => p.start === selectedSpeakerTimecode.value
      )
      if (partToEditInTranscript) {
        partToEditInTranscript.speaker = speaker
        partToEditInTranscript.words?.forEach(
          (w: Word) => (w.speaker = speaker)
        )
      }

      if (!getters.isEditable.value) {
        const db = await initDB()
        if (!db || !db.value) {
          return
        }
        await putTranscript(db, getters.transcript.value)
      }

      const partToEditInEditor = editorTranscript.value.parts.find(
        (p: Word) => p.start === selectedSpeakerTimecode.value
      )

      if (partToEditInEditor) {
        partToEditInEditor.speaker = speaker
        partToEditInEditor.words?.forEach(w => (w.speaker = speaker))
      }
    }

    const hideTagPopupAndDisableTagPicker = (e: MouseEvent) => {
      const isTagsSectionClicked = !!e.composedPath().find((p: EventTarget) => {
        const el = p as HTMLElement
        return el.id === 'all-tags-section'
      })

      if (!isTagsSectionClicked) {
        actions.finishTagEdit()
        closeTagPopup()
      }
    }

    function handlePaste(e: ClipboardEvent) {
      pasteFromClipboard(e)
    }

    function handlePartFocus(part: Word) {
      setMouseCursor(part)
    }

    async function saveTranscript() {
      //
    }

    function initAutoSave() {
      autosaveInterval.value = (setInterval(async () => {
        if (getters.isEditable.value) {
          await saveTranscript()
        } else {
          clearInterval(autosaveInterval.value)
        }
      }, 2000) as unknown) as number
    }

    function getPartsByWords() {
      if (!displayedTranscript.value) {
        return
      }
      partsWords.value = displayedTranscript.value.parts.map(
        p => p.words || getWords(p)
      )
      version.value += 1
    }

    watch(getters.shouldSave, async (newValue, prevValue) => {
      if (!newValue && prevValue !== newValue) {
        // Turn off edit
        const newTranscript = formatAll(false)
        actions.updateTranscript(newTranscript)
        actions.reinitializeTags(newTranscript?.words)
        version.value += 1

        const db = await initDB()
        if (!db || !db.value || !newTranscript) {
          return
        }
        await putTranscript(db, newTranscript)
      } else {
        // Turn on edit
      }
    })

    watch(textEditor.saveTranscript, async value => {
      if (value) {
        await saveTranscript()
        textEditor.saveTranscript.value = false
      }
    })

    watch(getters.shouldUndo, (newValue, prevValue) => {
      if (newValue && prevValue !== newValue) {
        textEditor.undo()
      }
    })

    watch(displayedTranscript, getPartsByWords)
    onMounted(() => {
      getPartsByWords()
    })
    watch(transcript, () => {
      if (transcript.value) {
        // displayedTranscript.value = getters.transcript.value
        updTranscript(transcript.value)
      }
    })
    // watch(version, () => {
    // if (getters.transcript.value) {
    //     displayedTranscript.value = getters.transcript.value
    //     updTranscript(getters.transcript.value)
    // }
    // })

    actions.registerAudioRewindCallback(navigateToWord)

    return {
      activateTagsPicker,
      hideSelectionPopup,
      handleTextSelection,
      handleWordClick,
      setPlayerStatus,
      handleTagHover,
      handleTagLeave,
      handleTagClick,
      setSpeaker,
      handleTagDelete,
      handleTopicEdit,
      closeTagPopup,
      upvote,
      downvote,
      handleTagPopupLeave,
      hideTagPopupAndDisableTagPicker,
      showSpeakers,
      handlePartFocus,
      handleEditorInput,
      handleEditorKeydown,
      isTagPopupShown,
      activeSpeakerBtn,
      tagPopupTarget,
      speakerPopupTarget,
      isPlaying,
      conversationBg,
      audioTime,
      player,
      version,
      partsWords,
      ...actions,
      isSpeakersListVisible: getters.isSpeakersListVisible,
      selectionPopupShown: getters.selectionPopupShown,
      selectionBoundaries: getters.selectionBoundaries,
      analysisActive: getters.analysisActive,
      isEditable: getters.isEditable,
      spelledWords: getters.spelledWords,
      isTimecodePopupShown,
      handlePaste,
      wordPopupTarget,
      hideWordPopup,
      shownWord,
      displayedTranscript,
      selectedTag,
      conversationRef,
      isDownvotePopupVisible,
      showDownvotePopup,
      hideDownvotePopup,
      downvotePopupTarget,
      subCategoryOptions,
      NONE_SUB_CATEGORY
    }
  }
})
